import { Component, inject, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatStepper } from '@angular/material/stepper';
import { MysqlService } from 'app/services/mysql.service';
import moment from 'moment';

@Component({
  selector: 'app-agos-public-page',
  templateUrl: './agos-public-page.component.html',
  styleUrl: './agos-public-page.component.scss',
  standalone: false
})

export class AgosPublicPageComponent implements OnInit {

  fb = inject(FormBuilder);
  db = inject(MysqlService);

  recapA = [];
  recapB = [];

  isSending = false;
  isSubmitted = false;
  appuntamentoNextAttempted = false;
  duplicatePracticeKey = '';

  contractNrAndPlateNotValid: boolean = false;
  minPreferredDate: Date = this.getTomorrowDate();

  isCheckingPlateAndContract = false;

  frm = this.fb.group({
    contrattoStep: this.fb.group({
      contratto: ['', [Validators.required]],
      targa: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(7), Validators.pattern(/^[A-Z0-9]+$/)]],
      marca: [''],
      modello: [''],
      allestimento: [''],
      telaio: [''],
      scadenza: ['']
    }),

    contattiStep: this.fb.group({
      cliente: ['', [Validators.required]],
      telefono: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      contattoPreferito: ['', [Validators.required]]
    }),

    appuntamentoStep: this.fb.group({
      data1: ['', [Validators.required]],
      fascia1: ['', [Validators.required]],
      data2: [''],
      fascia2: [''],
      data3: [''],
      fascia3: [''],
      denominazioneDealer: [''],
      indirizzoDealer: [''],
      comuneDealer: [''],
      capDealer: ['']
    }),

    noteStep: this.fb.group({
      note: [''],
      consenso: [false, [Validators.requiredTrue]]
    })
  });

  get contrattoGroup(): FormGroup {
    return this.frm.get('contrattoStep') as FormGroup;
  }

  get contattiGroup(): FormGroup {
    return this.frm.get('contattiStep') as FormGroup;
  }

  get appuntamentoGroup(): FormGroup {
    return this.frm.get('appuntamentoStep') as FormGroup;
  }

  get noteGroup(): FormGroup {
    return this.frm.get('noteStep') as FormGroup;
  }

  get completionPercent(): number {
    const required = [
      this.control('contrattoStep.contratto').valid,
      this.control('contrattoStep.targa').valid,
      this.control('contattiStep.cliente').valid,
      this.control('contattiStep.telefono').valid,
      this.control('contattiStep.email').valid,
      this.control('contattiStep.contattoPreferito').valid,
      this.control('appuntamentoStep.data1').valid,
      this.control('appuntamentoStep.fascia1').valid,
      this.control('noteStep.consenso').valid
    ];

    return Math.round((required.filter(Boolean).length / required.length) * 100);
  }

  isMobile = false;


  constructor(private snackBar: MatSnackBar) { }

  ngOnInit() {
    this.checkMobile();

    window.addEventListener('resize', () => this.checkMobile());
  }


  checkMobile() {
    this.isMobile = window.innerWidth <= 900;
  }

  getTomorrowDate(): Date {
    const tomorrow = new Date();
    tomorrow.setHours(0, 0, 0, 0);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow;
  }

  sanitizeTarga(): void {
    const ctrl = this.control('contrattoStep.targa');

    const normalized = String(ctrl.value ?? '').trim().toUpperCase();

    if (ctrl.value !== normalized) {
      ctrl.setValue(normalized);
    }
  }

  setCombinationErrorOnControl(control: AbstractControl, shouldSet: boolean) {
    const currentErrors = { ...(control.errors ?? {}) };

    if (shouldSet) {
      currentErrors['combinationNotFound'] = true;
    } else {
      delete currentErrors['combinationNotFound'];
    }

    const hasErrors = Object.keys(currentErrors).length > 0;
    control.setErrors(hasErrors ? currentErrors : null);
  }

  setContractAndPlateCombinationInvalidState(isInvalid: boolean) {
    const contrattoCtrl = this.control('contrattoStep.contratto');
    const targaCtrl = this.control('contrattoStep.targa');

    this.contractNrAndPlateNotValid = isInvalid;
    this.setCombinationErrorOnControl(contrattoCtrl, isInvalid);
    this.setCombinationErrorOnControl(targaCtrl, isInvalid);

    if (isInvalid) {
      contrattoCtrl.markAsTouched();
      targaCtrl.markAsTouched();
    }
  }

  getFieldError(path: string): string | null {
    const ctrl = this.control(path);

    if (!ctrl || !ctrl.errors || (!ctrl.touched && !ctrl.dirty)) return null;

    if (ctrl.hasError('required')) return 'Campo obbligatorio';
    if (ctrl.hasError('minlength') || ctrl.hasError('pattern')) return 'Formato non valido';
    if (ctrl.hasError('email')) return 'Email non valida';

    return null;
  }

  clearVehicleAndDealerData() {
    this.contrattoGroup.patchValue({
      marca: '',
      modello: '',
      allestimento: '',
      telaio: '',
      scadenza: ''
    });

    this.appuntamentoGroup.patchValue({
      denominazioneDealer: '',
      indirizzoDealer: '',
      comuneDealer: '',
      capDealer: ''
    });
  }

  onContractOrPlateInputChanged() {
    this.setContractAndPlateCombinationInvalidState(false);
    this.clearVehicleAndDealerData();
  }

  onContractOrPlateBlur() {
    this.sanitizeTarga();

    const contratto = String(this.contrattoGroup.get('contratto')?.value ?? '').trim();
    const targaCtrl = this.control('contrattoStep.targa');

    if (!contratto || targaCtrl.invalid) {
      return;
    }

    void this.searchByPlateAndContract({ showNotFoundMessage: true });
  }

  showNotFoundPopup = false;

  async searchByPlateAndContract(options: { focusOnInvalid?: boolean, showNotFoundMessage?: boolean } = {}): Promise<boolean> {

    const focusOnInvalid = options.focusOnInvalid ?? false;
    const showNotFoundMessage = options.showNotFoundMessage ?? false;

    this.isCheckingPlateAndContract = true;

    this.sanitizeTarga();

    const contratto = String(this.contrattoGroup.get('contratto')?.value ?? '').trim();
    const targa = String(this.contrattoGroup.get('targa')?.value ?? '').trim().toUpperCase();

    if (!contratto || !targa) {
      this.setContractAndPlateCombinationInvalidState(false);
      this.isCheckingPlateAndContract = false;
      return false;
    }

    try {

      const response: any = await this.db.agos.get_by_plate_and_contract_pub(targa, contratto);

      console.log('Risposta:', response);

      const res: AgosVehicle = response?.list?.[0];

      if (!res) {

        this.setContractAndPlateCombinationInvalidState(true);
        this.clearVehicleAndDealerData();
        if (focusOnInvalid) {
          this.takeUserUpOnFormInvalid(this.contrattoGroup);
        }

        if (showNotFoundMessage) {
          this.snackBar.open(
            'Nessun risultato per questa combinazione di targa e contratto.',
            'Chiudi',
            { duration: 6000, panelClass: ['snackbar-error'] }
          );
        }

        this.isCheckingPlateAndContract = false;
        return false;
      }

      this.setContractAndPlateCombinationInvalidState(false);
      this.isCheckingPlateAndContract = false;

      const scadenza = res.DtFinePrevista
        ? new Date(res.DtFinePrevista)
        : null;

      this.contrattoGroup.patchValue({
        marca: res.MarcaVeicolo ?? '',
        modello: res.ModelloVeicolo ?? '',
        allestimento: res.AllestimentoVeicolo ?? '',
        telaio: res.Telaio ?? '',
        scadenza: scadenza
      });

      this.appuntamentoGroup.patchValue({
        denominazioneDealer: res.DenominazioneDealer ?? '',
        indirizzoDealer: res.IndirizzoDealer ?? '',
        comuneDealer: res.ComuneDealer ?? '',
        capDealer: res.CapDealer ?? ''
      });

      return true;

    } catch (err) {
      console.error(err);
      this.isCheckingPlateAndContract = false;
      return false;
    }
  }

  showDuplicatePopup = false;
  async checkExistingPracticeByPlateAndContract(targa: string, contratto: string, showDialog = false) {

    const normalizedTarga = String(targa ?? '').trim().toUpperCase();
    const normalizedContratto = String(contratto ?? '').trim().toUpperCase();

    if (!normalizedTarga || !normalizedContratto) {
      return false;
    }

    try {
      const res: any = await this.db.agos.list_fleet(normalizedTarga);
      const list = res?.list ? res.list : [];

      const exists = list.some((row: any) => {
        const rowTarga = String(row?.Targa ?? row?.targa ?? '').trim().toUpperCase();
        const rowContratto = String(row?.CodContratto ?? row?.codContratto ?? '').trim().toUpperCase();
        const idPerizia = +(row?.idPerizia || row?.id || 0);

        return rowTarga == normalizedTarga && rowContratto == normalizedContratto && idPerizia > 0;
      });

      if (exists && showDialog) {
        this.showDuplicatePopup = true;
      }

      return exists;

    } catch (err) {
      console.error('Errore verifica pratica esistente', err);
      return false;
    }
  }

  closeDuplicatePopup() {
    this.showDuplicatePopup = false;
  }

  takeUserUpOnFormInvalid(formGroup: FormGroup): void {
    const getFirstInvalidControlName = (group: FormGroup): string | null => {
      for (const name of Object.keys(group.controls)) {
        const control = group.controls[name];

        if (control.invalid) {
          if (control instanceof FormGroup) {
            return getFirstInvalidControlName(control);
          }
          return name;
        }
      }
      return null;
    };

    const invalidControlName = getFirstInvalidControlName(formGroup);

    if (invalidControlName) {
      const invalidElement = document.querySelector(
        `[formControlName="${invalidControlName}"], [name="${invalidControlName}"]`
      ) as HTMLElement | null;

      if (invalidElement) {
        const targetElement = invalidElement.closest('mat-form-field') || invalidElement;
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

        setTimeout(() => invalidElement.focus(), 300);
      }
    }
  }

  async onContrattoStepNext(stepper: MatStepper) {

    if (this.isCheckingPlateAndContract) {
      return;
    }

    if (this.contrattoGroup.invalid) {
      this.contrattoGroup.markAllAsTouched();
      this.takeUserUpOnFormInvalid(this.contrattoGroup);
      return;
    }

    const valid = await this.searchByPlateAndContract({ focusOnInvalid: true, showNotFoundMessage: true });

    if (!valid) {
      return;
    }

    const contratto = this.contrattoGroup.get('contratto')?.value;
    const targa = this.contrattoGroup.get('targa')?.value;

    const exists = await this.checkExistingPracticeByPlateAndContract(
      targa,
      contratto,
      true
    );

    if (exists) {
      return;
    }

    stepper.next();
  }

  onContattiGroupStepNext(stepper: MatStepper): void {

    if (this.contattiGroup.invalid) {
      this.contattiGroup.markAllAsTouched();
      this.takeUserUpOnFormInvalid(this.contattiGroup);
      return;
    }
    stepper.next();
  }

  onAppuntamentoStepNext(stepper: MatStepper): void {
    this.appuntamentoNextAttempted = true;

    if (this.appuntamentoGroup.invalid) {
      this.appuntamentoGroup.markAllAsTouched();
      this.takeUserUpOnFormInvalid(this.appuntamentoGroup);
      return;
    }
    stepper.next();
  }

  showAppuntamentoError(path: string): boolean {
    const ctrl = this.control(path);
    return this.appuntamentoNextAttempted && ctrl.invalid;
  }

  onStepperSelectionChange(): void {
    setTimeout(() => {
      const activeHeader = document.querySelector('.agos-stepper .mat-step-header[aria-selected="true"]') as HTMLElement | null;

      if (!activeHeader) {
        return;
      }

      activeHeader.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    });
  }

  isInvalid(path: string): boolean {
    const ctrl = this.control(path);
    return ctrl.invalid && (ctrl.touched || ctrl.dirty);
  }

  resetAll(): void {
    this.frm.reset();
    this.isSubmitted = false;
    this.appuntamentoNextAttempted = false;
    this.recapA.splice(0, this.recapA.length);
    this.recapB.splice(0, this.recapB.length);
  }

  control(path: string): AbstractControl {
    return this.frm.get(path) as AbstractControl;
  }

  async submit(): Promise<void> {
    if (this.frm.invalid) {
      this.frm.markAllAsTouched();
      return;
    }

    const contratto = this.contrattoGroup.value;
    const contatti = this.contattiGroup.value;
    const appuntamento = this.appuntamentoGroup.value;
    const note = this.noteGroup.value;

    const formatDate = (value: any): string => {
      if (!value) {
        return '-';
      }

      const date = moment(value);

      return date.isValid()
        ? date.format('DD/MM/YYYY')
        : '-';
    };

    const formatText = (value: any): string => {
      return String(value ?? '').trim() || '-';
    };

    const marca = formatText(contratto.marca);
    const modello = formatText(contratto.modello);
    const marcaModello = [marca !== '-' ? marca : '', modello !== '-' ? modello : '']
      .filter(Boolean)
      .join(' ') || '-';

    this.recapA.splice(0, this.recapA.length,
      {
        key: 'Numero contratto',
        value: formatText(contratto.contratto)
      },
      {
        key: 'Targa',
        value: formatText(contratto.targa)
      },
      {
        key: 'Marca / Modello',
        value: marcaModello
      },
      {
        key: 'Allestimento',
        value: formatText(contratto.allestimento)
      },
      {
        key: 'Telaio',
        value: formatText(contratto.telaio)
      },
      {
        key: 'Scadenza contratto',
        value: formatDate(contratto.scadenza)
      }
    );


    this.recapB.splice(0, this.recapB.length,
      {
        key: 'Cliente',
        value: formatText(contatti.cliente)
      },
      {
        key: 'Telefono',
        value: formatText(contatti.telefono)
      },
      {
        key: 'Email',
        value: formatText(contatti.email)
      },
      {
        key: 'Contatto preferito',
        value: formatText(contatti.contattoPreferito)
      },
      {
        key: 'Dealer',
        value: formatText(appuntamento.denominazioneDealer)
      },
      {
        key: 'Indirizzo dealer',
        value:
          [
            appuntamento.indirizzoDealer,
            appuntamento.comuneDealer,
            appuntamento.capDealer
          ]
            .map(v => String(v ?? '').trim())
            .filter(Boolean)
            .join(', ') || '-'
      },
      {
        key: 'Prima preferenza',
        value: `${formatDate(appuntamento.data1)} - ${formatText(appuntamento.fascia1)}`
      },
      {
        key: 'Seconda preferenza',
        value: `${formatDate(appuntamento.data2)} - ${formatText(appuntamento.fascia2)}`
      },
      {
        key: 'Terza preferenza',
        value: `${formatDate(appuntamento.data3)} - ${formatText(appuntamento.fascia3)}`
      },
      {
        key: 'Note',
        value: formatText(note.note)
      }
    );

    const payload: AgosFormPayload = {
      contratto: contratto.contratto!,
      targa: contratto.targa!,

      cliente: contatti.cliente!,
      telefono: contatti.telefono!,
      email: contatti.email!,
      contattoPreferito: contatti.contattoPreferito ?? '',

      appuntamenti: {
        preferenza1: {
          data: appuntamento.data1,
          fascia: appuntamento.fascia1!
        },
        preferenza2: {
          data: appuntamento.data2,
          fascia: appuntamento.fascia2 ?? ''
        },
        preferenza3: {
          data: appuntamento.data3,
          fascia: appuntamento.fascia3 ?? ''
        }
      },

      note: note.note ?? '',
      consenso: note.consenso!
    };

    this.isSending = true;

    try {
      const res: any = await this.db.agos.create_pub(payload);
      console.log('create_pub res: ', res);

      if (res.status !== 'OK') {
        const msg = res?.msg || res?.message || 'Si è verificato un errore durante l\'invio. Riprova tra qualche istante.';

        this.snackBar.open(
          String(msg) + "\n\n",
          'Chiudi',
          { duration: 6000, panelClass: ['snackbar-error'] }
        );
        this.isSending = false;
        return;

      }
      this.isSubmitted = true;
      this.isSending = false;

    } catch (err) {

      console.error('Errore invio richiesta Agos', err);

      this.snackBar.open(
        'Si è verificato un errore durante l\'invio. Riprova tra qualche istante.' + "\n\n",
        'Chiudi',
        { duration: 6000, panelClass: ['snackbar-error'] }
      );
      this.isSending = false;
    }
  }

  onKeydownEnter(event: Event): void {
    const target = event.target as HTMLElement;

    if (target.tagName.toLowerCase() !== 'textarea') {
      event.preventDefault();
    }
  }

}


export interface AgosVehicle {
  CodContratto: string
  DtInizioEffettiva: string
  DtFinePrevista: string
  DtFineEffettiva: any
  CodCliente: string
  DenominazioneCliente: string
  TelCliente: any
  CellCliente: string
  EmailCliente: string
  EmailVerbaliCliente: any
  PecCliente: any
  DenominazioneUtilizzatore: string
  TelUtilizzatore: any
  CellUtilizzatore: string
  EmailUtilizzatore: string
  EmailSecondariaUtilizzatore: any
  MarcaVeicolo: string // Consider using enum
  ModelloVeicolo: string
  AllestimentoVeicolo: string
  Targa: string
  Telaio: string
  DtImmatricolazione: string
  UltimoKm: number
  DtUltimoKm: string
  DenominazioneDealer: string
  IndirizzoDealer: string
  ComuneDealer: string // Consider using enum
  CapDealer: string
  CodPaeseDealer: string // Consider using enum
  TelefonoDealer: string
  PartitaIvaDealer: string
  EmailDealer: string
  sinistri: string
  created_at: string
  idPerizia: any
  stato: any
}

export interface SearchByPlateResponse {
  status: "OK" | "ERROR"
  list: AgosVehicle[]
}


export interface AgosFormPayload {
  contratto: string;
  targa: string;

  cliente: string;
  telefono: string;
  email: string;
  contattoPreferito: string;

  appuntamenti: {
    preferenza1: { data: string | null; fascia: string };
    preferenza2: { data: string | null; fascia: string };
    preferenza3: { data: string | null; fascia: string };
  };

  note: string;
  consenso: boolean;
}


